import {
  LIST_STALE_TIME_MS,
  useApiClient,
  useFeatureFlags,
  useIsFeatureFlagActive,
  usePermissions,
} from '@klareospace/pharos-lib-front-utils';
import { USER_PREFERENCE_CATEGORY } from '@klareospace/pharos-lib-types';
import type { OnboardingWizardInfo, UserPreference } from '@klareospace/pharos-lib-types';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '&/auth';
import { FEATURE_FLAGS } from '&/feature-flags';
import { useUserPreferences } from '&/features/user-preferences';
import { createOnboardingWizardApi, onboardingWizardKeys } from '../api';
import { ONBOARDING_WIZARD_SKIPPED_PREFERENCE_KEY } from '../constants';

// ─── Constants ────────────────────────────────────────────────────────────────

const ONBOARDING_REQUIRED_PERMISSIONS = [
  'tenants:manage',
  'handling-unit-types:manage',
  'vehicles:manage',
  'users:manage',
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOnboardingGateResult {
  /** `infoQuery.dataUpdatedAt` — changes identity whenever a fresh fetch/refetch lands, 0 while never fetched. */
  dataUpdatedAt: number;
  /** True once eligible and not yet completed, REGARDLESS of whether the user skipped. Used by the
   * wizard page's own entry guard so a skipped-but-incomplete admin navigating to `/onboarding`
   * directly is never bounced — only ineligible/completed users are. */
  isAccessible: boolean;
  /** The backend onboarding-wizard-info payload, once successfully fetched. Kept from the last
   * successful fetch across a later refetch error (TanStack Query v5 preserves `data` on refetch
   * error) — see `resolveGateResult`'s refetch-blip handling. */
  info: OnboardingWizardInfo | undefined;
  /** True while a background refetch of onboarding-wizard-info is in flight (info is already defined). */
  isFetching: boolean;
  /** True while the eligibility decision (now including the skip preference) is still being determined. */
  isLoading: boolean;
  /** True once the user has dismissed the wizard via Skip — see `ONBOARDING_WIZARD_SKIPPED_PREFERENCE_KEY`. */
  isSkipped: boolean;
  /** True once determined that the onboarding wizard should be auto-shown/auto-redirected to —
   * `isAccessible && !isSkipped`. A skipped user never gets auto-redirected here, but can still
   * reach the wizard by navigating to it directly (see `isAccessible`). */
  shouldShowWizard: boolean;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Encapsulates the onboarding wizard display decision: feature flag, impersonation,
 * required permissions, the backend onboarding-wizard-info completion state, and the user's own
 * Skip choice. The info and skip-preference queries are only enabled once all upstream gates pass,
 * so neither fires while impersonating, while the flag is inactive, or while permissions are missing.
 */
export function useOnboardingGate(): UseOnboardingGateResult {
  const { isAuthenticated, isImpersonating, isLoading: isAuthLoading } = useAuth();
  const canCheckEligibility = isAuthenticated && !isImpersonating;

  const isFlagActive = useIsFeatureFlagActive(FEATURE_FLAGS.DSP_TENANT_ONBOARDING, canCheckEligibility);
  const { isLoading: isFlagsLoading } = useFeatureFlags(canCheckEligibility);

  const { hasAllPermissions, isLoading: isPermissionsLoading, isReady: isPermissionsReady } = usePermissions();
  const hasRequiredPermissions = hasAllPermissions(ONBOARDING_REQUIRED_PERMISSIONS);

  const gatesPassed = canCheckEligibility && isFlagActive && isPermissionsReady && hasRequiredPermissions;

  const client = useApiClient('admin-auth');
  const infoQuery: UseQueryResult<OnboardingWizardInfo> = useQuery({
    enabled: gatesPassed,
    queryFn: () => createOnboardingWizardApi(client).getOnboardingWizardInfo(),
    queryKey: onboardingWizardKeys.info(),
    staleTime: LIST_STALE_TIME_MS,
  });
  const preferencesQuery = useUserPreferences(
    { keys: [ONBOARDING_WIZARD_SKIPPED_PREFERENCE_KEY] },
    { enabled: gatesPassed },
  );

  return resolveGateResult({
    gatesPassed,
    infoQuery,
    isAuthLoading,
    isEligible: canCheckEligibility,
    isFlagsLoading,
    isPermissionsLoading,
    preferencesQuery,
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

interface ResolveGateResultParams {
  gatesPassed: boolean;
  infoQuery: UseQueryResult<OnboardingWizardInfo>;
  isAuthLoading: boolean;
  isEligible: boolean;
  isFlagsLoading: boolean;
  isPermissionsLoading: boolean;
  preferencesQuery: UseQueryResult<UserPreference[]>;
}

function resolveIsSkipped(preferences: UserPreference[] | undefined): boolean {
  return (
    preferences?.some(
      (pref) =>
        pref.category === USER_PREFERENCE_CATEGORY.FRONTOFFICE &&
        pref.key === ONBOARDING_WIZARD_SKIPPED_PREFERENCE_KEY &&
        pref.value === true,
    ) ?? false
  );
}

function buildPendingResult(isLoading: boolean): UseOnboardingGateResult {
  return {
    dataUpdatedAt: 0,
    info: undefined,
    isAccessible: false,
    isFetching: false,
    isLoading,
    isSkipped: false,
    shouldShowWizard: false,
  };
}

function resolveGateResult({
  gatesPassed,
  infoQuery,
  isAuthLoading,
  isEligible,
  isFlagsLoading,
  isPermissionsLoading,
  preferencesQuery,
}: ResolveGateResultParams): UseOnboardingGateResult {
  if (isAuthLoading || !isEligible) {
    return buildPendingResult(isAuthLoading);
  }

  if (isFlagsLoading || isPermissionsLoading) {
    return buildPendingResult(true);
  }

  if (!gatesPassed) {
    return buildPendingResult(false);
  }

  return resolveGateResultFromQueries(infoQuery, preferencesQuery);
}

/** Once every upstream gate has passed, the decision comes down to the info and skip-preference queries. */
function resolveGateResultFromQueries(
  infoQuery: UseQueryResult<OnboardingWizardInfo>,
  preferencesQuery: UseQueryResult<UserPreference[]>,
): UseOnboardingGateResult {
  const { data, dataUpdatedAt, isError, isFetching } = infoQuery;
  const isSkipped = resolveIsSkipped(preferencesQuery.data);

  // Initial-load failure — no previously-fetched data to fall back on — stays fail-closed.
  if (isError && data === undefined) {
    return {
      dataUpdatedAt,
      info: undefined,
      isAccessible: false,
      isFetching,
      isLoading: false,
      isSkipped,
      shouldShowWizard: false,
    };
  }

  // A REFETCH error (e.g. the active invalidation fired right after a successful step-1 submit)
  // must not eject the user while previously-fetched data is still cached — TanStack Query v5
  // keeps `data` from the last successful fetch on a refetch error, so `data` being defined here
  // means the decision keeps coming from that cached snapshot regardless of `isError`. The skip
  // preference joins the same loading discipline so the redirect decision never flashes.
  if (data === undefined || preferencesQuery.isLoading) {
    return {
      dataUpdatedAt,
      info: undefined,
      isAccessible: false,
      isFetching,
      isLoading: true,
      isSkipped,
      shouldShowWizard: false,
    };
  }

  const isAccessible = !data.completed;
  // Mirrors the info query's fail-closed discipline: if we can't confirm the skip preference, never
  // auto-show/auto-redirect — a previously-skipped admin must not get force-redirected back into the
  // wizard just because the preference lookup errored. `isAccessible` (used for DIRECT navigation to
  // /onboarding) is unaffected — only the auto-redirect decision is suppressed.
  const shouldShowWizard = isAccessible && !isSkipped && !preferencesQuery.isError;
  return { dataUpdatedAt, info: data, isAccessible, isFetching, isLoading: false, isSkipped, shouldShowWizard };
}
